// src/pages/TasksPage.tsx
// Card dashboard layout for task management
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  IonIcon,
  IonFab,
  IonFabButton,
  IonSkeletonText,
  IonToggle,
  IonItem,
  IonLabel,
} from '@ionic/react';
import {
  add,
  checkmarkCircle,
  checkmarkCircleOutline,
  star,
  calendarOutline,
} from 'ionicons/icons';
import { supabase } from '../services/supabase';
import type { TaskAssignment, Task, FamilyMember, ScheduleTemplate, TaskList, ListType } from '../types';

// UI Components
import { PageShell } from '../components/ui/PageShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Segment, type SegmentOption } from '../components/ui/Segment';
import { Badge } from '../components/ui/Badge';
import { Modal, ModalSection } from '../components/ui/Modal';
import { FormSelect, type SelectOption } from '../components/ui/FormSelect';
import { TextInput } from '../components/ui/TextInput';
import { IconPicker } from '../components/ui/IconPicker';

// Layout Primitives
import { Container, Stack, AutoGrid, Row } from '../components/ui/Layout';

// Task Dashboard Cards
import {
  TasksOverviewCard,
  TasksQuickActionsCard,
  MyTasksCard,
  RoutinesCard,
  FamilyProgressCard,
  ListsPanel,
  ListsCard,
  RoutineEditorModal,
  TaskDetailModal,
} from '../components/tasks';

// Hooks
import { useScheduleTemplates, useFamilyMembers, useTaskLists } from '../hooks';

// Auth
import { useAuth, useHouseholdId } from '../contexts/AuthContext';

import './TasksPage.css';

// Completion sound - using Web Audio API for better performance
const playCompletionSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    // Create a pleasant "ding" sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch {
    // Audio not supported, fail silently
  }
};

// Random celebration emojis for completion animation
const CELEBRATION_EMOJIS = ['🎉', '⭐', '🌟', '✨', '💫', '🏆', '👏', '🙌', '💪', '🔥', '💯', '🎊'];

type TaskFilter = 'today' | 'week' | 'all';

const FILTER_OPTIONS: SegmentOption<TaskFilter>[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'all', label: 'All Tasks' },
];

// Helper to get date string in YYYY-MM-DD format (local timezone, not UTC)
const toDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Get date range for week
const getWeekRange = (): { start: string; end: string } => {
  const now = new Date();
  const start = toDateString(now);
  const end = new Date(now);
  end.setDate(end.getDate() + 7);
  return { start, end: toDateString(end) };
};

// Format due date for display
const formatDueDate = (dateString: string): string => {
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const TasksPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { loading: authLoading, user } = useAuth();
  const householdId = useHouseholdId();
  const searchParams = new URLSearchParams(location.search);
  const shouldOpenAddModal = searchParams.get('action') === 'add';

  const [filter, setFilter] = useState<TaskFilter>('today');
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  // Use hooks for family members and schedule templates
  const { members: familyMembers, loading: membersLoading } = useFamilyMembers();
  const {
    templates,
    loading: templatesLoading,
    generateAssignments,
    create: createTemplate,
    update: updateTemplate,
    remove: deleteTemplate,
    addItem: addTemplateItem,
    updateItem: updateTemplateItem,
    removeItem: removeTemplateItem,
    reorderItems: reorderTemplateItems,
  } = useScheduleTemplates();

  // Task lists
  const { lists: taskLists, loading: listsLoading } = useTaskLists();

  // Get current member (family member linked to current user)
  const currentMember = useMemo(() => {
    if (!user?.id || !familyMembers.length) return null;
    return familyMembers.find(m => m.user_id === user.id) || null;
  }, [user?.id, familyMembers]);

  // Panels
  const [showListsPanel, setShowListsPanel] = useState(false);

  // Routine editor state
  const [showRoutineEditor, setShowRoutineEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ScheduleTemplate | null>(null);

  // Task detail modal state
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<TaskAssignment | null>(null);

  // Animation state
  const [celebratingId, setCelebratingId] = useState<string | null>(null);
  const [celebrationEmojis, setCelebrationEmojis] = useState<{ id: number; emoji: string; x: number; y: number }[]>([]);
  const celebrationIdCounter = useRef(0);

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    taskId: '',
    assignedTo: '',
    dueDate: toDateString(new Date()),
  });

  // Quick add task state
  const [showQuickAddTask, setShowQuickAddTask] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [taskSearchValue, setTaskSearchValue] = useState('');
  const [quickTaskForm, setQuickTaskForm] = useState({
    name: '',
    icon: '📋',
    points_value: 1,
    saveAsTemplate: false,  // Default to one-time task, not a reusable template
  });

  // Open add modal if URL param present
  useEffect(() => {
    if (shouldOpenAddModal) {
      setShowAssignModal(true);
      history.replace('/tasks');
    }
  }, [shouldOpenAddModal, history]);

  // Fetch data - only when householdId is available
  const fetchData = useCallback(async () => {
    if (!householdId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const today = toDateString(new Date());
    const { end } = getWeekRange();

    // Fetch assignments: include overdue incomplete tasks + upcoming tasks
    // We need two queries: one for overdue incomplete, one for upcoming
    const [overdueRes, upcomingRes, tasksRes] = await Promise.all([
      // Overdue incomplete tasks (past due dates, not completed)
      supabase
        .from('task_assignments')
        .select('*, task:tasks(*), chore:tasks(*), assignee:family_members!assigned_to(*)')
        .eq('household_id', householdId)
        .lt('due_date', today)
        .eq('is_completed', false)
        .order('due_date')
        .order('due_time', { nullsFirst: false }),
      // Upcoming tasks within the week (including today)
      supabase
        .from('task_assignments')
        .select('*, task:tasks(*), chore:tasks(*), assignee:family_members!assigned_to(*)')
        .eq('household_id', householdId)
        .gte('due_date', today)
        .lte('due_date', end)
        .order('due_date')
        .order('due_time', { nullsFirst: false }),
      supabase
        .from('tasks')
        .select('*')
        .eq('household_id', householdId)
        .eq('is_active', true)
        .order('sort_order'),
    ]);

    // Merge overdue and upcoming assignments, removing duplicates
    const allAssignments = [...(overdueRes.data || []), ...(upcomingRes.data || [])];
    const uniqueAssignments = allAssignments.filter((a, index, self) =>
      index === self.findIndex(t => t.id === a.id)
    );
    setAssignments(uniqueAssignments);
    if (tasksRes.data) setTasks(tasksRes.data);

    setLoading(false);
  }, [householdId]);

  useEffect(() => {
    if (!householdId) return;
    fetchData();

    const channel = supabase
      .channel('tasks-page')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignments' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'family_members' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchData, householdId]);

  // Filter assignments based on selection
  const filteredAssignments = useMemo(() => {
    const today = toDateString(new Date());
    const { end } = getWeekRange();

    if (filter === 'today') {
      // Show today's tasks PLUS overdue incomplete tasks
      return assignments.filter(a =>
        a.due_date === today || (a.due_date < today && !a.is_completed)
      );
    }
    if (filter === 'week') {
      // Show this week's tasks PLUS overdue incomplete tasks
      return assignments.filter(a =>
        (a.due_date >= today && a.due_date <= end) || (a.due_date < today && !a.is_completed)
      );
    }
    return assignments;
  }, [assignments, filter]);

  // Stats
  const stats = useMemo(() => {
    const today = toDateString(new Date());
    // Include overdue incomplete tasks in "today" stats
    const todayAssignments = assignments.filter(a =>
      a.due_date === today || (a.due_date < today && !a.is_completed)
    );
    const completed = todayAssignments.filter(a => a.is_completed).length;
    const pointsEarned = assignments
      .filter(a => a.is_completed)
      .reduce((sum, a) => sum + ((a.task || a.chore)?.points_value || 0), 0);

    // Calculate streak (consecutive days with all tasks completed)
    // For now, simplified to just show 0 or calculate later
    const streak = 0;

    // Count overdue tasks separately for display
    const overdueCount = assignments.filter(a => a.due_date < today && !a.is_completed).length;

    return {
      dueToday: todayAssignments.filter(a => !a.is_completed).length,
      completed,
      total: todayAssignments.length,
      pointsEarned,
      streak,
      overdueCount,
    };
  }, [assignments]);

  // Trigger celebration animation
  const triggerCelebration = (assignmentId: string, element: HTMLElement) => {
    setCelebratingId(assignmentId);
    playCompletionSound();

    // Get element position for emoji spawning
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Spawn random emojis
    const newEmojis: typeof celebrationEmojis = [];
    const numEmojis = 6 + Math.floor(Math.random() * 4);

    for (let i = 0; i < numEmojis; i++) {
      const id = celebrationIdCounter.current++;
      const emoji = CELEBRATION_EMOJIS[Math.floor(Math.random() * CELEBRATION_EMOJIS.length)];
      const angle = (Math.PI * 2 * i) / numEmojis + (Math.random() - 0.5) * 0.5;
      const distance = 30 + Math.random() * 50;

      newEmojis.push({
        id,
        emoji,
        x: centerX + Math.cos(angle) * distance,
        y: centerY + Math.sin(angle) * distance,
      });
    }

    setCelebrationEmojis(prev => [...prev, ...newEmojis]);

    setTimeout(() => {
      setCelebratingId(null);
      setCelebrationEmojis(prev => prev.filter(e => !newEmojis.find(n => n.id === e.id)));
    }, 1000);
  };

  // Toggle task completion
  // Note: `newCompletedState` is the desired new state (true = completing, false = uncompleting)
  const toggleComplete = async (assignmentId: string, newCompletedState: boolean, event: React.MouseEvent) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return;

    const taskData = assignment.task || assignment.chore;
    const pointsDelta = taskData?.points_value || 0;

    if (newCompletedState) {
      triggerCelebration(assignmentId, event.currentTarget as HTMLElement);
    }

    // Optimistic update
    setAssignments(prev =>
      prev.map(a =>
        a.id === assignmentId
          ? {
              ...a,
              is_completed: newCompletedState,
              completed_at: newCompletedState ? new Date().toISOString() : undefined,
            }
          : a
      )
    );

    // Update in database
    const { error } = await supabase
      .from('task_assignments')
      .update({
        is_completed: newCompletedState,
        completed_at: newCompletedState ? new Date().toISOString() : null,
      })
      .eq('id', assignmentId);

    if (error) {
      fetchData();
    } else if (pointsDelta && assignment.assigned_to) {
      const delta = newCompletedState ? pointsDelta : -pointsDelta;
      await supabase.rpc('increment_points', {
        member_id: assignment.assigned_to,
        points_delta: delta,
      });
    }
  };

  // Quick actions handlers
  const handleAssignTask = () => setShowAssignModal(true);
  const handleNewList = () => {
    // For now, open lists panel - future: open create list modal
    setShowListsPanel(true);
  };
  const handleNewRoutine = () => {
    // Future: open routine editor modal
    history.push('/settings?tab=chores');
  };
  const handleManage = () => {
    history.push('/settings?tab=chores');
  };

  // Routine handlers
  const handleApplyRoutine = async (template: ScheduleTemplate) => {
    if (!householdId) return;
    const today = toDateString(new Date());
    await generateAssignments(template.id, today);
    fetchData();
  };

  const handleEditRoutine = (template: ScheduleTemplate) => {
    setEditingTemplate(template);
    setShowRoutineEditor(true);
  };

  const handleCreateRoutine = () => {
    setEditingTemplate(null);
    setShowRoutineEditor(true);
  };

  const handleCloseRoutineEditor = () => {
    setShowRoutineEditor(false);
    setEditingTemplate(null);
  };

  // Inline quick add for My Tasks card
  const handleInlineQuickAdd = async (taskName: string) => {
    if (!householdId || !currentMember) return;

    const today = toDateString(new Date());

    // Create task and assignment in one go
    const { data: newTask, error: taskError } = await supabase
      .from('tasks')
      .insert({
        household_id: householdId,
        name: taskName.trim(),
        icon: '📋',
        points_value: 1,
        frequency: 'as_needed',
        is_active: false, // One-time personal task
        task_type: 'todo',
      })
      .select()
      .single();

    if (taskError) {
      console.error('Failed to create task:', taskError);
      return;
    }
    if (!newTask) return;

    // Create assignment for current member
    const { error: assignError } = await supabase.from('task_assignments').insert({
      household_id: householdId,
      task_id: newTask.id,
      assigned_to: currentMember.id,
      due_date: today,
    });

    if (assignError) {
      console.error('Failed to create assignment:', assignError);
    }

    fetchData();
  };

  // Handle clicking on an existing task (opens detail modal)
  const handleTaskClick = (assignment: TaskAssignment) => {
    setSelectedAssignment(assignment);
    setShowTaskDetail(true);
  };

  // Handle task detail update
  const handleTaskDetailUpdate = async (data: {
    taskName?: string;
    assignedTo?: string;
    dueDate?: string;
    dueTime?: string;
    startDate?: string;
    startTime?: string;
    notes?: string;
    icon?: string;
    pointsValue?: number;
  }): Promise<boolean> => {
    if (!selectedAssignment) return false;

    // Update assignment fields
    const assignmentPayload: Record<string, unknown> = {};

    if (data.assignedTo !== undefined) assignmentPayload.assigned_to = data.assignedTo;
    if (data.dueDate !== undefined) assignmentPayload.due_date = data.dueDate;
    if (data.dueTime !== undefined) assignmentPayload.due_time = data.dueTime;
    if (data.notes !== undefined) assignmentPayload.notes = data.notes;
    if (data.startDate && data.startTime) {
      assignmentPayload.actual_start_time = `${data.startDate}T${data.startTime}:00`;
    } else if (data.startDate) {
      assignmentPayload.actual_start_time = `${data.startDate}T00:00:00`;
    }

    // Update assignment if there are changes
    if (Object.keys(assignmentPayload).length > 0) {
      const { error } = await supabase
        .from('task_assignments')
        .update(assignmentPayload)
        .eq('id', selectedAssignment.id);

      if (error) {
        console.error('Failed to update assignment:', error);
        return false;
      }
    }

    // Update task fields (name, icon, points) if changed
    const taskPayload: Record<string, unknown> = {};
    if (data.taskName !== undefined) taskPayload.name = data.taskName;
    if (data.icon !== undefined) taskPayload.icon = data.icon;
    if (data.pointsValue !== undefined) taskPayload.points_value = data.pointsValue;

    if (Object.keys(taskPayload).length > 0 && selectedAssignment.task_id) {
      const { error: taskError } = await supabase
        .from('tasks')
        .update(taskPayload)
        .eq('id', selectedAssignment.task_id);

      if (taskError) {
        console.error('Failed to update task:', taskError);
        return false;
      }
    }

    fetchData();
    return true;
  };

  // Handle task detail delete
  const handleTaskDetailDelete = async () => {
    if (!selectedAssignment) return;

    const { error } = await supabase
      .from('task_assignments')
      .delete()
      .eq('id', selectedAssignment.id);

    if (error) {
      console.error('Failed to delete assignment:', error);
      return;
    }

    setShowTaskDetail(false);
    setSelectedAssignment(null);
    fetchData();
  };

  // Create task for routine editor
  const handleCreateTaskForRoutine = async (taskData: { name: string; icon: string; points_value: number }): Promise<Task | null> => {
    if (!householdId) return null;

    const { data: newTask, error } = await supabase
      .from('tasks')
      .insert({
        household_id: householdId,
        name: taskData.name,
        icon: taskData.icon,
        points_value: taskData.points_value,
        frequency: 'as_needed',
        is_active: true,
        task_type: 'chore',
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to create task:', error);
      return null;
    }

    // Add to local tasks list
    if (newTask) {
      setTasks(prev => [...prev, newTask]);
    }

    return newTask;
  };

  // List handlers
  const handleListSelect = (list: TaskList) => {
    setShowListsPanel(false);
    history.push(`/lists/${list.id}`);
  };

  const handleCreateList = (type?: ListType) => {
    setShowListsPanel(false);
    history.push(`/lists/new${type ? `?type=${type}` : ''}`);
  };

  // Quick add task handlers
  const handleCreateTaskFromSelect = (name: string) => {
    setQuickTaskForm(f => ({ ...f, name: name.trim() }));
    setShowQuickAddTask(true);
    setTaskSearchValue('');
  };

  const createQuickTask = async () => {
    if (!householdId || !quickTaskForm.name.trim()) return;

    const { data: newTask, error: taskError } = await supabase
      .from('tasks')
      .insert({
        household_id: householdId,
        name: quickTaskForm.name.trim(),
        icon: quickTaskForm.icon || '📋',
        points_value: quickTaskForm.points_value,
        frequency: 'as_needed',
        is_active: quickTaskForm.saveAsTemplate,
        task_type: quickTaskForm.saveAsTemplate ? 'chore' : 'todo',
      })
      .select()
      .single();

    if (taskError) {
      console.error('Failed to create task:', taskError);
      return;
    }

    if (newTask) {
      if (quickTaskForm.saveAsTemplate) {
        setTasks(prev => [...prev, newTask]);
      }
      setAssignForm(f => ({ ...f, taskId: newTask.id }));
      setQuickTaskForm({ name: '', icon: '📋', points_value: 1, saveAsTemplate: false });
      setShowQuickAddTask(false);
      setTaskSearchValue('');
    }
  };

  const createAssignment = async () => {
    if (!householdId || !assignForm.taskId || !assignForm.assignedTo || !assignForm.dueDate) return;

    const { error } = await supabase.from('task_assignments').insert({
      household_id: householdId,
      task_id: assignForm.taskId,
      assigned_to: assignForm.assignedTo,
      due_date: assignForm.dueDate,
    });

    if (!error) {
      setShowAssignModal(false);
      setAssignForm({
        taskId: '',
        assignedTo: '',
        dueDate: toDateString(new Date()),
      });
      fetchData();
    }
  };

  // Select options
  const taskOptions: SelectOption<string>[] = tasks.map(t => ({
    id: t.id,
    label: t.name,
    sublabel: `${t.points_value} pts`,
    icon: <span style={{ fontSize: 20 }}>{t.icon}</span>,
    value: t.id,
  }));

  const memberOptions: SelectOption<string>[] = familyMembers.map(m => ({
    id: m.id,
    label: m.nickname || m.name,
    sublabel: m.is_parent ? 'Parent' : undefined,
    icon: m.avatar_url ? (
      <img
        src={m.avatar_url}
        alt=""
        style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
      />
    ) : (
      <span style={{ fontSize: 20 }}>{m.avatar_emoji || '👤'}</span>
    ),
    value: m.id,
  }));

  const dateOptions: SelectOption<string>[] = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    const dateStr = toDateString(date);
    return {
      id: dateStr,
      label: formatDueDate(dateStr),
      value: dateStr,
    };
  });

  // Show loading state
  if (authLoading || !householdId) {
    return (
      <PageShell title="Tasks" headerType="page" showSettings noPadding>
        <Container size="lg" padding="md">
          <Stack gap="lg">
            <AutoGrid minColumnWidth="200px" gap="md">
              <IonSkeletonText animated style={{ width: '100%', height: 140, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 140, borderRadius: 12 }} />
            </AutoGrid>
            <AutoGrid minColumnWidth="200px" gap="md">
              <IonSkeletonText animated style={{ width: '100%', height: 200, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 200, borderRadius: 12 }} />
            </AutoGrid>
            <IonSkeletonText animated style={{ width: '100%', height: 180, borderRadius: 12 }} />
          </Stack>
        </Container>
      </PageShell>
    );
  }

  // FAB element - rendered via PageShell's renderAfterContent for proper positioning
  // edge prop ensures proper safe area handling with tab bar
  const fabElement = (
    <IonFab vertical="bottom" horizontal="end" slot="fixed" edge={false} style={{
      // Offset from bottom to clear the tab bar
      marginBottom: 'calc(var(--ion-safe-area-bottom, 0px) + 60px)',
      marginRight: 16,
    }}>
      <IonFabButton onClick={() => setShowAssignModal(true)}>
        <IonIcon icon={add} />
      </IonFabButton>
    </IonFab>
  );

  return (
    <PageShell
      title="Tasks"
      headerType="page"
      showSettings
      noPadding
      renderAfterContent={fabElement}
    >
      <Container size="lg" padding="md">
        <Stack gap="lg">
          {/* Filter Row */}
          <Segment
            options={FILTER_OPTIONS}
            value={filter}
            onChange={setFilter}
            fullWidth
            size="sm"
          />

          {/* Dashboard Cards Grid */}
          <div className="tasks-dashboard">
            {/* Row 1: Overview + Quick Actions */}
            <TasksOverviewCard
              assignments={filteredAssignments}
              familyMembers={familyMembers}
              currentUserId={user?.id}
              filter={filter}
            />

            <TasksQuickActionsCard
              onAssignTask={handleAssignTask}
              onNewList={handleNewList}
              onNewRoutine={handleNewRoutine}
              onManage={handleManage}
            />

            {/* Row 2: My Tasks + Routines */}
            <MyTasksCard
              assignments={filteredAssignments}
              currentMember={currentMember}
              onToggle={toggleComplete}
              onQuickAdd={handleInlineQuickAdd}
              onTaskClick={handleTaskClick}
              showCompleted={false}
            />

            <RoutinesCard
              templates={templates}
              loading={templatesLoading}
              onApplyToday={handleApplyRoutine}
              onEdit={handleEditRoutine}
              onCreate={handleCreateRoutine}
            />

            <ListsCard
              lists={taskLists}
              loading={listsLoading}
              onListClick={(list) => history.push(`/lists/${list.id}`)}
              onCreateList={() => history.push('/lists/new')}
              onViewAll={() => setShowListsPanel(true)}
            />

            {/* Row 3: Family Progress (Full Width) */}
            <div className="tasks-dashboard__full-width">
              <FamilyProgressCard
                assignments={filteredAssignments}
                familyMembers={familyMembers}
                onMemberClick={(member) => history.push(`/family/${member.id}`)}
                onTaskClick={handleTaskClick}
                onTaskToggle={toggleComplete}
                showUnassigned
                expandable
              />
            </div>
          </div>
        </Stack>
      </Container>

      {/* Celebration emojis overlay */}
      <div className="celebration-overlay" aria-hidden="true">
        {celebrationEmojis.map(emoji => (
          <span
            key={emoji.id}
            className="celebration-emoji"
            style={{
              left: emoji.x,
              top: emoji.y,
            }}
          >
            {emoji.emoji}
          </span>
        ))}
      </div>

      {/* Lists Panel */}
      <ListsPanel
        isOpen={showListsPanel}
        onClose={() => setShowListsPanel(false)}
        lists={taskLists}
        loading={loading}
        onListSelect={handleListSelect}
        onCreateList={handleCreateList}
      />

      {/* Assign Task Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setShowQuickAddTask(false);
        }}
        size="md"
        header={{ title: 'Assign Task' }}
        footer={{
          primaryAction: {
            label: 'Assign',
            onClick: createAssignment,
            disabled: !assignForm.taskId || !assignForm.assignedTo || !assignForm.dueDate,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => {
              setShowAssignModal(false);
              setShowQuickAddTask(false);
            },
          },
        }}
      >
        <ModalSection spacing="normal">
          <Stack gap="md">
            {!showQuickAddTask ? (
              <FormSelect<string>
                label="Task"
                value={taskSearchValue}
                onChange={setTaskSearchValue}
                options={taskOptions}
                selectedOption={taskOptions.find(t => t.value === assignForm.taskId) || null}
                onSelect={opt => {
                  setAssignForm(f => ({ ...f, taskId: opt.value }));
                  setTaskSearchValue('');
                }}
                onClear={() => {
                  setAssignForm(f => ({ ...f, taskId: '' }));
                  setTaskSearchValue('');
                }}
                placeholder="Search or type to create..."
                variant="default"
                clearable
                searchable
                creatable
                onCreate={handleCreateTaskFromSelect}
                createLabel="Create task"
              />
            ) : (
              <Card style={{ padding: 16, background: 'var(--ion-color-light)' }}>
                <Stack gap="sm">
                  <div style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ion-color-dark)',
                    marginBottom: 4
                  }}>
                    Quick Add Task
                  </div>
                  <TextInput
                    label="Task Name"
                    value={quickTaskForm.name}
                    onChange={(v) => setQuickTaskForm(f => ({ ...f, name: v }))}
                    placeholder="e.g., Clean room"
                    variant="filled"
                  />
                  <Row gap="md" align="start">
                    <div style={{ flex: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--ion-color-medium)' }}>
                        Icon
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowIconPicker(true)}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          border: '2px solid var(--ion-border-color)',
                          background: 'var(--ion-card-background)',
                          fontSize: 24,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'border-color 0.2s ease',
                        }}
                        className="icon-picker-button"
                      >
                        {quickTaskForm.icon}
                      </button>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--ion-color-medium)' }}>
                        Points
                      </div>
                      <Row gap="xs" align="center">
                        {[1, 2, 3, 5, 10].map(pts => (
                          <button
                            key={pts}
                            type="button"
                            onClick={() => setQuickTaskForm(f => ({ ...f, points_value: pts }))}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              border: quickTaskForm.points_value === pts
                                ? '2px solid var(--kff-forge-gold)'
                                : '1px solid var(--ion-border-color)',
                              background: quickTaskForm.points_value === pts
                                ? 'var(--kff-forge-gold-10)'
                                : 'transparent',
                              color: quickTaskForm.points_value === pts
                                ? 'var(--kff-forge-gold)'
                                : 'var(--ion-text-color)',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {pts}
                          </button>
                        ))}
                      </Row>
                    </div>
                  </Row>
                  <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0' }}>
                    <IonLabel style={{ fontSize: 14 }}>
                      Save for reuse
                      <p style={{ fontSize: 11, color: 'var(--ion-color-medium)', margin: '2px 0 0 0' }}>
                        Add to task library for future assignments
                      </p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={quickTaskForm.saveAsTemplate}
                      onIonChange={(e) => setQuickTaskForm(f => ({ ...f, saveAsTemplate: e.detail.checked }))}
                    />
                  </IonItem>
                  <Row gap="sm" style={{ marginTop: 8 }}>
                    <Button
                      variant="primary"
                      fill="clear"
                      size="small"
                      onClick={() => setShowQuickAddTask(false)}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={createQuickTask}
                      disabled={!quickTaskForm.name.trim()}
                      style={{ flex: 1 }}
                    >
                      Create & Select
                    </Button>
                  </Row>
                </Stack>
              </Card>
            )}

            <FormSelect<string>
              label="Assign To"
              value=""
              onChange={() => {}}
              options={memberOptions}
              selectedOption={memberOptions.find(m => m.value === assignForm.assignedTo) || null}
              onSelect={opt => setAssignForm(f => ({ ...f, assignedTo: opt.value }))}
              placeholder="Select family member..."
              variant="default"
              clearable={false}
              searchable={false}
            />

            <FormSelect<string>
              label="Due Date"
              value=""
              onChange={() => {}}
              options={dateOptions}
              selectedOption={dateOptions.find(d => d.value === assignForm.dueDate) || null}
              onSelect={opt => setAssignForm(f => ({ ...f, dueDate: opt.value }))}
              placeholder="Select due date..."
              variant="default"
              clearable={false}
              searchable={false}
            />
          </Stack>
        </ModalSection>
      </Modal>

      {/* Icon Picker Modal */}
      <Modal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        header={{ title: 'Choose Icon' }}
        size="md"
      >
        <ModalSection spacing="normal">
          <IconPicker
            value={{ type: 'emoji', name: quickTaskForm.icon }}
            onChange={(val) => {
              setQuickTaskForm(f => ({
                ...f,
                icon: val?.type === 'emoji' ? val.name : '📋'
              }));
              setShowIconPicker(false);
            }}
            tabs={['emoji']}
            iconSize={32}
            maxHeight={400}
          />
        </ModalSection>
      </Modal>

      {/* Routine Editor Modal */}
      <RoutineEditorModal
        isOpen={showRoutineEditor}
        onClose={handleCloseRoutineEditor}
        template={editingTemplate}
        tasks={tasks}
        familyMembers={familyMembers}
        onCreate={createTemplate}
        onUpdate={updateTemplate}
        onDelete={deleteTemplate}
        onAddItem={addTemplateItem}
        onUpdateItem={updateTemplateItem}
        onRemoveItem={removeTemplateItem}
        onReorderItems={reorderTemplateItems}
        onCreateTask={handleCreateTaskForRoutine}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={showTaskDetail}
        onClose={() => {
          setShowTaskDetail(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        familyMembers={familyMembers}
        householdId={householdId || ''}
        onUpdate={handleTaskDetailUpdate}
        onDelete={handleTaskDetailDelete}
      />
    </PageShell>
  );
};

export default TasksPage;
